import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SearchType } from '../interfaces/interfaces';

type MatomoCommand = [string, ...unknown[]];
const ANONYMOUS_SESSION_ID_KEY = 'stencas_matomo_session_id';
const HEARTBEAT_PING_INTERVAL_MS = 15_000;

export type SearchAnalyticsFilters = {
  categories?: string[];
  dateFrom?: string;
  dateTo?: string;
  fileType?: string;
};

declare global {
  interface Window {
    _paq?: MatomoCommand[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  private initialized = false;
  private skippedInitialRoute = false;
  private heartbeatPingIntervalId: number | null = null;
  private readonly pendingSearches = new Map<SearchType, string>();

  init(): void {
    if (
      this.initialized ||
      !isPlatformBrowser(this.platformId) ||
      !environment.matomo.enabled ||
      !environment.matomo.url ||
      !environment.matomo.siteId
    ) {
      return;
    }

    this.initialized = true;
    const paq = this.getQueue();
    const matomoUrl = this.normalizeUrl(environment.matomo.url);

    paq.push(['disableCookies']);
    paq.push(['setDoNotTrack', true]);
    const anonymousSessionId = this.getAnonymousSessionId();
    if (anonymousSessionId) {
      paq.push(['setVisitorId', anonymousSessionId]);
      paq.push([
        'setCustomRequestProcessing',
        (request: string) =>
          this.withAnonymousSessionParams(request, anonymousSessionId),
      ]);
    }
    paq.push(['enableLinkTracking']);
    paq.push(['enableHeartBeatTimer', 15]);
    paq.push(['setTrackerUrl', `${matomoUrl}matomo.php`]);
    paq.push(['setSiteId', environment.matomo.siteId]);
    paq.push(['trackPageView']);

    this.loadScript(`${matomoUrl}matomo.js`);
    this.startHeartbeatPings();
    this.trackRouteChanges();
  }

  markSearchSubmitted(type: SearchType, query: string | undefined): void {
    const sanitizedQuery = this.sanitizeSearchQuery(query);

    if (!sanitizedQuery) {
      this.pendingSearches.delete(type);
      return;
    }

    this.pendingSearches.set(type, sanitizedQuery);
  }

  trackPendingSiteSearch(
    type: SearchType,
    query: string | undefined,
    totalResults: number,
    filters: SearchAnalyticsFilters = {},
  ): void {
    const sanitizedQuery = this.sanitizeSearchQuery(query);

    if (!sanitizedQuery || this.pendingSearches.get(type) !== sanitizedQuery) {
      return;
    }

    this.pendingSearches.delete(type);
    this.trackSiteSearch(
      sanitizedQuery,
      type === 'global' ? 'global' : type,
      totalResults,
    );
    this.trackSearchResultsEvent(type, totalResults, filters);
  }

  trackSiteSearch(
    query: string | undefined,
    searchCategory: string | false,
    totalResults: number,
  ): void {
    const sanitizedQuery = this.sanitizeSearchQuery(query);

    if (!sanitizedQuery) {
      return;
    }

    this.push([
      'trackSiteSearch',
      sanitizedQuery,
      searchCategory,
      totalResults,
    ]);
  }

  trackDownload(fileUrl: string | undefined): void {
    if (!fileUrl) {
      return;
    }

    this.push(['trackLink', fileUrl, 'download']);
  }

  private trackSearchResultsEvent(
    type: SearchType,
    totalResults: number,
    filters: SearchAnalyticsFilters,
  ): void {
    const searchFiltersDimensionId =
      environment.matomo.dimensions.searchFilters;

    if (searchFiltersDimensionId > 0) {
      this.push([
        'setCustomDimension',
        searchFiltersDimensionId,
        this.buildSearchFiltersValue(type, totalResults, filters),
      ]);
    }

    this.push([
      'trackEvent',
      'Search',
      'Results',
      type,
      totalResults,
    ]);

    if (searchFiltersDimensionId > 0) {
      this.push(['deleteCustomDimension', searchFiltersDimensionId]);
    }
  }

  private buildSearchFiltersValue(
    type: SearchType,
    totalResults: number,
    filters: SearchAnalyticsFilters,
  ): string {
    const categories = [...(filters.categories ?? [])]
      .filter(Boolean)
      .sort();

    return [
      `type:${type}`,
      `categories:${categories.length ? categories.join('|') : 'none'}`,
      `date:${filters.dateFrom || filters.dateTo ? 'true' : 'false'}`,
      `file_type:${filters.fileType || 'none'}`,
      `results:${this.getResultBucket(totalResults)}`,
    ].join(';');
  }

  private getResultBucket(totalResults: number): string {
    if (totalResults <= 0) {
      return '0';
    }

    if (totalResults <= 5) {
      return '1-5';
    }

    if (totalResults <= 20) {
      return '6-20';
    }

    if (totalResults <= 100) {
      return '21-100';
    }

    return '100+';
  }

  sanitizeSearchQuery(query: string | undefined): string | null {
    const sanitized = (query ?? '')
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/\b[\w.%+-]+@[\w.-]+\.[A-Za-z]{2,}\b/g, '[redacted-email]')
      .replace(/\b(?:https?:\/\/|www\.)\S+\b/gi, '[redacted-url]')
      .replace(/\b\d{13}\b/g, '[redacted-id]')
      .replace(/(?:\+?\d[\d\s()./-]{6,}\d)/g, (match) =>
        match.replace(/\D/g, '').length >= 7 ? '[redacted-phone]' : match,
      )
      .slice(0, 100)
      .trim();

    return sanitized || null;
  }

  private trackRouteChanges(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      )
      .subscribe((event) => {
        if (!this.skippedInitialRoute) {
          this.skippedInitialRoute = true;
          return;
        }

        this.push(['setCustomUrl', event.urlAfterRedirects]);
        this.push(['setDocumentTitle', this.document.title]);
        this.push(['trackPageView']);
      });
  }

  private push(command: MatomoCommand): void {
    if (
      !isPlatformBrowser(this.platformId) ||
      !environment.matomo.enabled ||
      !environment.matomo.siteId
    ) {
      return;
    }

    this.getQueue().push(command);
  }

  private getQueue(): MatomoCommand[] {
    const defaultView = this.document.defaultView;

    if (!defaultView) {
      return [];
    }

    defaultView._paq = defaultView._paq ?? [];
    return defaultView._paq;
  }

  private loadScript(src: string): void {
    if (this.document.getElementById('matomo-tracker')) {
      return;
    }

    const script = this.document.createElement('script');
    script.id = 'matomo-tracker';
    script.async = true;
    script.src = src;
    this.document.head.appendChild(script);
  }

  private normalizeUrl(url: string): string {
    return url.endsWith('/') ? url : `${url}/`;
  }

  private startHeartbeatPings(): void {
    const defaultView = this.document.defaultView;

    if (!defaultView || this.heartbeatPingIntervalId !== null) {
      return;
    }

    this.heartbeatPingIntervalId = defaultView.setInterval(() => {
      if (this.document.visibilityState === 'hidden') {
        return;
      }

      this.push(['ping']);
    }, HEARTBEAT_PING_INTERVAL_MS);
  }

  private withAnonymousSessionParams(
    request: string,
    sessionId: string,
  ): string {
    const hasLeadingQuestionMark = request.startsWith('?');
    const params = new URLSearchParams(
      hasLeadingQuestionMark ? request.slice(1) : request,
    );

    params.set('_id', sessionId);
    params.set('cid', sessionId);
    params.set('_idn', '0');

    return `${hasLeadingQuestionMark ? '?' : ''}${params.toString()}`;
  }

  private getAnonymousSessionId(): string | null {
    const defaultView = this.document.defaultView;

    if (!defaultView) {
      return null;
    }

    try {
      const existingId = defaultView.sessionStorage.getItem(
        ANONYMOUS_SESSION_ID_KEY,
      );

      if (existingId && /^[a-f0-9]{16}$/.test(existingId)) {
        return existingId;
      }

      const sessionId = this.generateAnonymousSessionId(defaultView);
      defaultView.sessionStorage.setItem(ANONYMOUS_SESSION_ID_KEY, sessionId);
      return sessionId;
    } catch {
      return null;
    }
  }

  private generateAnonymousSessionId(defaultView: Window): string {
    const bytes = new Uint8Array(8);
    defaultView.crypto?.getRandomValues(bytes);

    if (bytes.some(Boolean)) {
      return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
        '',
      );
    }

    return Array.from({ length: 16 }, () =>
      Math.floor(Math.random() * 16).toString(16),
    ).join('');
  }
}
